import { ConfigProvider, DatePicker, Form, Input, message, Modal, Select, Image, Timeline, Tag, Divider } from "antd";
import { Dispatch, SetStateAction, useEffect, useState, useRef } from "react";
import container from "@/lib/di/container";
import React from "react";
import { dayjsPipe } from "@/core/pipes/dayjs.pipe";
import { UserRequestModel, UserRequestDetailResponse, UserRequestStatusHistory } from "@/core/models/user-request.model";
import { UserRequestRepo } from "@/core/repos/tables/user-request.repo";
import { UserRequestStatus, UserRequestType } from "@/core/enums/global.enums";
import { requestTypePipe } from "@/core/pipes/request-type.pipe";
import { requestStatusPipe, requestStatusTagPipe } from "@/core/pipes/request-status.pipe";
import { MODAL_THEME } from "@/core/themes/modal.theme";
import { usePermissionStore } from "@/core/stores/permission.store";
import { SaveButton } from "@/app/home/components/table/SaveButton";
import { DEF_DATE_FORMAT, DEF_DATETIME_FORMAT } from "@/app/home/utils/global.utils";
import { UploadForm } from "@/app/home/components/upload/UploadForm";
import { UploadFormHandle } from "@/app/home/components/upload/UploadForm";
import AuthRepo from "@/core/repos/auth.repo";
import { GlobalUtils } from "@/app/home/utils/global.utils";
import { PERMISSIONS } from "@/core/enums/permissions.enum";
import { formatDateTimePipe } from "@/core/pipes/format-date.pipe";

const userRequestRepo = container.get(UserRequestRepo);
const authRepo = container.get(AuthRepo);

interface DetailModalProps {
    isModalOpen: boolean,
    setIsModalOpen: Dispatch<SetStateAction<boolean>>,
    data: UserRequestModel,
}

const INPUT_WIDTH = 450;
const REQUEST_TYPES = [
    UserRequestType.Leak,
    UserRequestType.WaterLoss,
    UserRequestType.WaterQuality,
    UserRequestType.Payment,
    UserRequestType.Meter,
    UserRequestType.Other,
]
const REQUEST_STATUS = [
    UserRequestStatus.Pending,
    UserRequestStatus.InProcess,
    UserRequestStatus.Completed,
    UserRequestStatus.Cancelled,
]

const SEVERITY_OPTIONS = [
    { value: "minor", label: "Nhẹ" },
    { value: "moderate", label: "Trung bình" },
    { value: "severe", label: "Nghiêm trọng" },
]

export const DetailModal: React.FC<DetailModalProps> = ({isModalOpen, setIsModalOpen, data}) => {
    const permissionStore = usePermissionStore();
    const claims = authRepo.useClaims();
    const [form] = Form.useForm<UserRequestModel>();
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [messageApi, contextHolder] = message.useMessage();
    const [detailData, setDetailData] = useState<UserRequestDetailResponse | null>(null);
    const [loadingDetail, setLoadingDetail] = useState<boolean>(false);
    const uploadFormRef = useRef<UploadFormHandle>(null);
    
    const canUpdateStatus = GlobalUtils.checkPerm(claims, PERMISSIONS.USERREQUEST_UPDATE_STATUS.key) || 
                            GlobalUtils.checkPerm(claims, PERMISSIONS.ADMIN.key);

    const loadDetailData = async () => {
        if (!data.id || data.id.length === 0) return;
        setLoadingDetail(true);
        try {
            const detail = await userRequestRepo.getDetail(data.id);
            if (detail) {
                setDetailData(detail);
                form.setFieldsValue(detail.request);
            }
        } catch (error) {
            console.error("Error loading detail:", error);
            messageApi.error({ content: "Không thể tải chi tiết yêu cầu" });
        } finally {
            setLoadingDetail(false);
        }
    };

    // Load detail data when modal opens
    useEffect(() => {
        if (isModalOpen && data.id && data.id.length > 0) {
            loadDetailData();
        } else {
            setDetailData(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isModalOpen, data.id]);

    // Handle data changes
    const handleDataChange = async () => {
        if (data.id && data.id.length > 0) {
            setIsEdit(true);
            if (!detailData) {
                await loadDetailData();
            } else {
                form.setFieldsValue(detailData.request);
            }
        } else {
            setIsEdit(false);
            form.setFieldsValue(data);
        }
    };

    useEffect(() => {
        if (!isModalOpen) return;
        handleDataChange();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, isModalOpen]);

    const handleSave = async () => {
        try {
            setLoading(true);
            const formData = await form.validateFields();
            
            if (isEdit) {
                formData.id = data.id;
                
                // If updating status, check permission
                if (formData.status && formData.status !== detailData?.request.status) {
                    if (!canUpdateStatus) {
                        messageApi.error({ content: "Bạn không có quyền cập nhật trạng thái yêu cầu" });
                        setLoading(false);
                        return;
                    }
                }

                const result = await userRequestRepo.update({...data, ...formData});
                
                // Save uploaded files
                if (uploadFormRef.current && data.id) {
                    await uploadFormRef.current.saveUploadedFiles(data.id);
                }

                if (result) {
                    messageApi.success({ content: "Đã cập nhật yêu cầu" });
                    setIsModalOpen(false);
                    // Reload detail data
                    await loadDetailData();
                } else {
                    messageApi.error({ content: "Cập nhật yêu cầu thất bại" });
                }
            } else {
                const result = await userRequestRepo.create({...data, ...formData});
                setIsModalOpen(false);
                if (result) {
                    messageApi.success({ content: "Thêm yêu cầu mới thành công" });
                } else {
                    messageApi.error({ content: "Thêm yêu cầu mới thất bại" });
                }
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setIsModalOpen(false);
    };

    // Get requestType for conditional field rendering
    const [requestType, setRequestType] = useState<string | UserRequestType>(data.requestType || "");
    
    useEffect(() => {
        const currentType = detailData?.request.requestType || data.requestType || "";
        setRequestType(currentType);
    }, [detailData, data.requestType]);
    
    const isLeakType = requestType === UserRequestType.Leak;
    const isOtherType = requestType === UserRequestType.Other;

    return (
        <ConfigProvider theme={MODAL_THEME}>
            {contextHolder}
            <Modal 
                open={isModalOpen} 
                onCancel={handleCancel} 
                width={1200}
                centered
                styles={{
                    mask: {
                        backdropFilter: 'blur(2px)',
                        backgroundColor: 'var(--primary-mask)'
                    }
                }}
                footer={[
                    <div key={1} className="w-full flex flex-row justify-end px-1.5">
                        <SaveButton isEdit={isEdit} loading={loading} onClick={handleSave}/>
                    </div>,
                ]}
            >
                <div className="flex flex-row w-full items-start justify-start select-none">
                    <p className="ml-2 text-2xl font-bold text-primary">THÔNG TIN CHI TIẾT</p>
                </div>
                
                {loadingDetail ? (
                    <div className="flex justify-center items-center py-10">
                        <p>Đang tải dữ liệu...</p>
                    </div>
                ) : (
                    <>
                        <div className="flex flex-row justify-center mt-6">
                            <Form<UserRequestModel>
                                name="basic"
                                className="flex flex-col items-end"
                                form={form}
                                layout="vertical"
                                disabled={isEdit && !permissionStore.page.edit}
                            >
                                <div className="flex">
                                    <div className="flex flex-col w-1/2 pr-4">
                                        <Form.Item<UserRequestModel>
                                            label="Mã yêu cầu"
                                            name="code"
                                        >
                                            <Input style={{width: INPUT_WIDTH}} disabled></Input>
                                        </Form.Item>
                                        <Form.Item<UserRequestModel>
                                            label="Loại yêu cầu"
                                            name="requestType"
                                        >
                                            <Select placeholder="Chọn" style={{width: INPUT_WIDTH}} disabled>
                                                {REQUEST_TYPES.map(type => (
                                                    <Select.Option key={type} value={type}>
                                                        {requestTypePipe(type)}
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                        <Form.Item<UserRequestModel>
                                            label="Tên khách hàng"
                                            name="name"
                                        >
                                            <Input style={{width: INPUT_WIDTH}} disabled></Input>
                                        </Form.Item>
                                        <Form.Item<UserRequestModel>
                                            label="Trạng thái xử lý"
                                            name="status"
                                        >
                                            <Select 
                                                showSearch
                                                placeholder="Chọn trạng thái" 
                                                style={{width: INPUT_WIDTH}}
                                                disabled={!canUpdateStatus || (detailData?.request.status === UserRequestStatus.Completed || 
                                                     detailData?.request.status === UserRequestStatus.Cancelled) || false}
                                            >
                                                {REQUEST_STATUS.map(status => (
                                                    <Select.Option key={status} value={status}>
                                                        {requestStatusTagPipe(status)}
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                        {isLeakType && (
                                            <>
                                                <Form.Item<UserRequestModel>
                                                    label="Vị trí"
                                                    name="location"
                                                >
                                                    <Input style={{width: INPUT_WIDTH}} placeholder="Nhập vị trí rò rỉ"></Input>
                                                </Form.Item>
                                                <Form.Item<UserRequestModel>
                                                    label="Mức độ nghiêm trọng"
                                                    name="severity"
                                                >
                                                    <Select placeholder="Chọn mức độ" style={{width: INPUT_WIDTH}}>
                                                        {SEVERITY_OPTIONS.map(opt => (
                                                            <Select.Option key={opt.value} value={opt.value}>
                                                                {opt.label}
                                                            </Select.Option>
                                                        ))}
                                                    </Select>
                                                </Form.Item>
                                            </>
                                        )}
                                        {isOtherType && (
                                            <Form.Item<UserRequestModel>
                                                label="Mô tả tùy chỉnh"
                                                name="customDescription"
                                            >
                                                <Input.TextArea 
                                                    style={{width: INPUT_WIDTH+18, height: 100, resize: "none"}} 
                                                    placeholder="Nhập mô tả tùy chỉnh"
                                                ></Input.TextArea>
                                            </Form.Item>
                                        )}
                                        <Form.Item<UserRequestModel>
                                            label="Mô tả"
                                            name="description"
                                        >
                                            <Input.TextArea 
                                                style={{width: INPUT_WIDTH+18, height: 150, resize: "none"}} 
                                                disabled
                                            ></Input.TextArea>
                                        </Form.Item>
                                    </div>
                                    <div className="flex flex-col w-1/2">
                                        <Form.Item<UserRequestModel>
                                            label="Địa chỉ"
                                            name="address"
                                        >
                                            <Input style={{width: INPUT_WIDTH}}></Input>
                                        </Form.Item>
                                        <Form.Item<UserRequestModel>
                                            label="Điện thoại liên hệ"
                                            name="phone"
                                        >
                                            <Input style={{width: INPUT_WIDTH}} disabled></Input>
                                        </Form.Item>
                                        <Form.Item<UserRequestModel>
                                            label="Mã hợp đồng"
                                            name="waterUserId"
                                        >
                                            <Input style={{width: INPUT_WIDTH}} placeholder="Nhập mã hợp đồng"></Input>
                                        </Form.Item>
                                        <Form.Item<UserRequestModel>
                                            label="Thời gian dự kiến hoàn thành"
                                            name="expectedResolutionTime"
                                            getValueProps={(i)=>({value: dayjsPipe(i)})}
                                        >
                                            <DatePicker 
                                                format={DEF_DATETIME_FORMAT}
                                                showTime
                                                placeholder="Chọn thời gian"
                                                style={{width: INPUT_WIDTH}}
                                            ></DatePicker>
                                        </Form.Item>
                                        <Form.Item<UserRequestModel>
                                            label="Tóm tắt giải quyết"
                                            name="resolutionSummary"
                                        >
                                            <Input.TextArea 
                                                style={{width: INPUT_WIDTH+18, height: 150, resize: "none"}} 
                                                placeholder="Nhập tóm tắt giải quyết"
                                            ></Input.TextArea>
                                        </Form.Item>
                                        <Form.Item<UserRequestModel>
                                            label="Thời gian gửi"
                                            name="createdAt"
                                            getValueProps={(i)=>({value: dayjsPipe(i)})}
                                        >
                                            <DatePicker format={DEF_DATE_FORMAT} disabled style={{width: INPUT_WIDTH}}></DatePicker>
                                        </Form.Item>
                                        <Form.Item<UserRequestModel>
                                            label="Người gửi"
                                            name="createdBy"
                                        >
                                            <Input style={{width: INPUT_WIDTH}} disabled></Input>
                                        </Form.Item>
                                        <Form.Item<UserRequestModel>
                                            label="Thời gian cập nhật"
                                            name="updatedAt"
                                            getValueProps={(i)=>({value: dayjsPipe(i)})}
                                        >
                                            <DatePicker format={DEF_DATE_FORMAT} disabled style={{width: INPUT_WIDTH}}></DatePicker>
                                        </Form.Item>
                                        <Form.Item<UserRequestModel>
                                            label="Người cập nhật"
                                            name="updatedBy"
                                        >
                                            <Input style={{width: INPUT_WIDTH}} disabled></Input>
                                        </Form.Item>
                                    </div>
                                </div>
                            </Form>
                        </div>

                        {/* Contract Information */}
                        {detailData?.contract && (
                            <>
                                <Divider />
                                <div className="px-2">
                                    <p className="text-lg font-semibold text-primary mb-3">THÔNG TIN HỢP ĐỒNG</p>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <p className="text-sm text-gray-600">Mã hợp đồng:</p>
                                            <p className="font-medium">{detailData.contract.code}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-600">Tên khách hàng:</p>
                                            <p className="font-medium">{detailData.contract.name}</p>
                                        </div>
                                        <div className="col-span-2">
                                            <p className="text-sm text-gray-600">Địa chỉ:</p>
                                            <p className="font-medium">{detailData.contract.address}</p>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Status History Timeline */}
                        {detailData?.statusHistory && detailData.statusHistory.length > 0 && (
                            <>
                                <Divider />
                                <div className="px-2">
                                    <p className="text-lg font-semibold text-primary mb-3">LỊCH SỬ TRẠNG THÁI</p>
                                    <Timeline
                                        items={detailData.statusHistory.map((history: UserRequestStatusHistory) => ({
                                            color: history.newStatus === UserRequestStatus.Completed ? 'green' :
                                                   history.newStatus === UserRequestStatus.Cancelled ? 'red' :
                                                   history.newStatus === UserRequestStatus.InProcess ? 'blue' : 'gray',
                                            children: (
                                                <div>
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <Tag color={
                                                            history.newStatus === UserRequestStatus.Completed ? 'success' :
                                                            history.newStatus === UserRequestStatus.Cancelled ? 'error' :
                                                            history.newStatus === UserRequestStatus.InProcess ? 'processing' : 'default'
                                                        }>
                                                            {requestStatusPipe(history.newStatus)}
                                                        </Tag>
                                                        <span className="text-sm text-gray-500">
                                                            {formatDateTimePipe(history.createdAt)}
                                                        </span>
                                                    </div>
                                                    {history.description && (
                                                        <p className="text-sm text-gray-700">{history.description}</p>
                                                    )}
                                                    <p className="text-xs text-gray-400 mt-1">Bởi: {history.createdBy}</p>
                                                </div>
                                            ),
                                        }))}
                                    />
                                </div>
                            </>
                        )}

                        {/* Attachments */}
                        {isEdit && data.id && (
                            <>
                                <Divider />
                                <div className="px-2">
                                    <UploadForm 
                                        ref={uploadFormRef as any}
                                        objectType="UserRequest"
                                        referenceId={data.id}
                                        text="File đính kèm"
                                    />
                                </div>
                            </>
                        )}

                        {/* Display existing attachments */}
                        {detailData?.attachments && detailData.attachments.length > 0 && (
                            <>
                                <Divider />
                                <div className="px-2">
                                    <p className="text-lg font-semibold text-primary mb-3">FILE ĐÍNH KÈM</p>
                                    <div className="grid grid-cols-4 gap-4">
                                        {detailData.attachments.map((attachment) => (
                                            <div key={attachment.id} className="border rounded p-2">
                                                {attachment.path && (
                                                    <Image
                                                        src={attachment.path}
                                                        alt={attachment.name}
                                                        className="w-full h-32 object-cover rounded"
                                                        preview={{
                                                            src: attachment.path,
                                                        }}
                                                    />
                                                )}
                                                <p className="text-sm mt-2 truncate">{attachment.name}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </>
                        )}
                    </>
                )}
                <div className="mb-5 mx-2 border-t border-dashed border-gray-300"></div>
            </Modal>
        </ConfigProvider>
    )
}
